/*
 eslint-disable react/jsx-one-expression-per-line, react/jsx-curly-newline, react/no-array-index-key
 */
import React, {
  useState,
  useEffect,
  useMemo,
  useCallback,
  lazy,
  Suspense,
} from 'react';

// Redux
import { useDispatch, useSelector } from 'react-redux';

// Material
import Box from '@material-ui/core/Box';
import Grid from '@material-ui/core/Grid';

// React Swipeable Views
import SwipeableViews from 'react-swipeable-views';

// Actions
import * as DialogsActions from 'store/ducks/dialogs/actions';

// Components
import { TabPanel } from 'components/TabPanel';
import { DialogueExport } from 'components';
import { CustomTabs, EntryResume } from 'components/ReportComponents';

// Types
import { ApplicationState } from 'store';
import {
  EntrySimplifiedInterface,
  EntryGroupedInterface,
} from 'store/ducks/reports/types';

// Helpers
import { returnDateAndTime } from 'helpers/date';
import { stableSort, getComparator } from 'helpers/tableSortLabels';
import { getPaymentType } from 'helpers/getPaymentType';

// Contants
import BALANCE from 'constants/balance';
import CONST_QRCODE from 'constants/qrcodes';
import TableToPrint from './components/TableToPrint';
import { TableChart, TableAgrupmentsChart } from './components';

// Styles
import useStyles from './chartReport.styles';

const TotalCountComponent = lazy(() =>
  import('components/TotalCountComponent'),
);

interface TableReportsInterface {
  periodString?: string;
  csvRef?: any;
  setTableCSV: (param: string) => void;
  tableReference: any;
  setCurrNavGuide: (param: number) => void;
  currNavGuide: number;
  entrysList?: EntrySimplifiedInterface[];
  entrysGroupedList?: EntryGroupedInterface[];
  setTableReference?: any;
}

type Props = TableReportsInterface;

const ChartReport: React.FC<Props> = ({
  periodString,
  csvRef,
  setTableCSV,
  tableReference,
  setCurrNavGuide,
  currNavGuide,
  entrysList,
  entrysGroupedList,
  setTableReference,
}) => {
  const classes = useStyles();
  const dispatch = useDispatch();

  const [isPrinting, setIsPrinting] = useState<any>(false);
  const [isMounted, setIsMounted] = useState<
    'initalState' | 'loading' | 'notLoading'
  >('initalState');
  const [isComponentMounted, setIsComponentMounted] = useState<any>(
    'initialValue',
  );

  const [simplePdfData, setSimplePdfData] = useState<{
    title: string;
    subtitle: string;
    filters: string;
    headers: any;
    rows: any;
  }>({
    title: '',
    subtitle: '',
    filters: '',
    headers: [],
    rows: [],
  });

  const dialog = useSelector((state: ApplicationState) => state.dialogs.dialog);

  const [Component, setComponent] = useState<any>(
    <TableChart
      entrysList={entrysList}
      isPrinting={isPrinting}
      isMounted={isMounted}
      setIsMounted={setIsMounted}
      isHistoryDatails={false}
    />,
  );

  const chartEntryReportSimplifiedList = useSelector(
    (state: ApplicationState) => state.reports.chartEntryReportSimplifiedList,
  );

  const selectedItems = useSelector(
    (state: ApplicationState) => state.groupingModal.selectedItems,
  );

  const transportOperatorSelected = useSelector(
    (state: ApplicationState) =>
      state.transportOperators.transportOperatorSelected,
  );

  const transportOperatorsList = useSelector(
    (state: ApplicationState) =>
      state.transportOperators.transportOperatorsList,
  );

  const totalValue =
    chartEntryReportSimplifiedList?.reduce(
      (total, item) => total + Number(item.value),
      0,
    ) || 0;

  const totalValueGrouped =
    entrysGroupedList?.reduce(
      (total, item) => total + Number(item.totalValue),
      0,
    ) || 0;

  const quantityEntryGrouped =
    entrysGroupedList?.reduce(
      (total, item) => total + Math.round(item.count),
      0,
    ) || 0;

  const quantityEntry = useMemo(
    () => chartEntryReportSimplifiedList?.length || 0,
    [chartEntryReportSimplifiedList],
  );

  const formatQrcodeType = useMemo(
    () => (qrcodeType: number) =>
      CONST_QRCODE.ENTRY.find((e) => e.KEY === qrcodeType)?.VALUE || '--',
    [],
  );

  useEffect(() => {
    let csv = '';
    if (currNavGuide === 0) {
      if (
        Array.isArray(chartEntryReportSimplifiedList) &&
        chartEntryReportSimplifiedList.length > 0
      ) {
        let header = `id;linha;beneficio;data;valor;tipo;qrcode;identificador;latitude;longitude;dbd;local;ID Usuario;Nome Usuario;`;
        if (transportOperatorSelected.type === 1) {
          header += ';Operação\n';
        } else {
          header += '\n';
        }

        csv += header;
        csv += chartEntryReportSimplifiedList
          .map(
            (entry: any) =>
              `${entry.id ? entry.id.substring(0, 6) : ''};${
                entry.line ? entry.line : ''
              };${entry.benefit ? entry.benefit : ''};${
                entry.createdAt ? returnDateAndTime(entry.createdAt) : ''
              };${
                entry.value
                  ? `R$ ${entry.value.toLocaleString('pt-BR', {
                      minimumFractionDigits: 2,
                    })}`
                  : 'R$ 0'
              };${
                entry.usageType
                  ? BALANCE.USAGE_TYPES.find(({ ID }) => ID === entry.usageType)
                      ?.VALUE || '--'
                  : ''
              };${formatQrcodeType(entry?.qrcodeType) ||
                ''};${entry?.description || '--'};${
                entry.lat ? entry.lat : ''
              };${entry.lng ? entry.lng : ''};${entry.dbd ? entry.dbd : ''};${
                entry.place ? entry.place : ''
              };${entry.userId ? entry.userId.substring(0, 6) : ''};${
                entry.userName ? entry.userName : ''
              };${
                transportOperatorSelected.type === 1
                  ? transportOperatorsList.find(
                      (e) => e.id === entry?.transportOperatorId,
                    )?.name + '\n' || '---'
                  : '\n'
              }`,
          )
          .join('');
      }
    } else {
      let header = `Posição${
        selectedItems.find((e) => e.key === 'usedDate') ? ';Data' : ''
      }${selectedItems.find((e) => e.key === 'dbdId') ? ';DBD' : ''}${
        selectedItems.find((e) => e.key === 'paymentType')
          ? ';Forma de pagamento'
          : ''
      }${
        selectedItems.find((e) => e.key === 'qrcodeType')
          ? ';Tipo de QRCode'
          : ''
      };Valor;Quatidade\n`;
      if (transportOperatorSelected.type === 1) {
        header += ';Operação\n';
      } else {
        header += '\n';
      }
      csv += header;

      if (entrysGroupedList) {
        csv += stableSort(entrysGroupedList, getComparator('asc', 'usedAt'))
          .map(
            (entry) =>
              `${entry.position || '--'}${
                selectedItems.find((e) => e.key === 'usedDate')
                  ? ';' + entry.usedDate || ''
                  : ''
              }${
                selectedItems.find((e) => e.key === 'dbdId')
                  ? ';' + entry.dbdId
                  : ''
              }${
                selectedItems.find((e) => e.key === 'paymentType')
                  ? ';' + getPaymentType(entry.paymentType)
                  : ''
              }${
                selectedItems.find((e) => e.key === 'qrcodeType')
                  ? ';' + formatQrcodeType(entry.qrcodeType)
                  : ''
              };${`R$ ${entry.totalValue}`};${entry.count};${
                transportOperatorSelected.type === 1
                  ? transportOperatorsList.find(
                      (e) => e.id === entry?.transportOperatorId,
                    )?.name + '\n' || '---'
                  : '\n'
              }`,
          )
          .join('');
      }
    }
    setTableCSV(csv);
  }, [
    currNavGuide,
    entrysList,
    selectedItems,
    entrysGroupedList,
    chartEntryReportSimplifiedList,
    setTableCSV,
  ]);

  useEffect(() => {
    const headerSimplePdf =
      currNavGuide === 0
        ? [
            'ID',
            'Linha',
            'Benefício',
            'Data',
            'Valor',
            'Tipo',
            'Qrcode',
            'Identificador',
            'Latitude',
            'Longitude',
            'DBD',
            'Local',
            'ID Usuário',
            'Nome Usuário',
          ]
        : ['Posição', 'Valor', 'Quantidade'];

    if (currNavGuide === 1) {
      if (selectedItems.find((e) => e.key === 'usedDate')) {
        headerSimplePdf.push('Data');
      }
      if (selectedItems.find((e) => e.key === 'dbdId')) {
        headerSimplePdf.push('DBD');
      }
      if (selectedItems.find((e) => e.key === 'paymentType')) {
        headerSimplePdf.push('Forma de pagamento');
      }
      if (selectedItems.find((e) => e.key === 'qrcodeType')) {
        headerSimplePdf.push('Tipo de QRCode');
      }
      if (transportOperatorSelected.type === 1) {
        headerSimplePdf.push('Operação');
      }
    }

    let rowsSimplePdf: string[][] = [];
    if (currNavGuide === 0) {
      if (chartEntryReportSimplifiedList.length > 0) {
        rowsSimplePdf = chartEntryReportSimplifiedList?.map((entry: any) => [
          entry.id ? String(entry.id.substring(0, 6)) : '-',
          entry.line ? String(entry.line) : '-',
          entry.benefit ? String(entry.benefit) : '-',
          entry.createdAt ? String(returnDateAndTime(entry.createdAt)) : '-',
          entry.value
            ? `R$ ${entry.value.toLocaleString('pt-BR', {
                minimumFractionDigits: 2,
              })}`
            : '-',
          entry.usageType ? String(entry.usageType) : '-',
          entry?.qrcodeType ? String(formatQrcodeType(entry?.qrcodeType)) : '-',
          entry?.description || '--',
          entry.lat ? String(entry.lat) : '-',
          entry.lng ? String(entry.lng) : '-',
          entry.dbd ? String(entry.dbd) : '-',
          entry.place ? String(entry.place) : '-',
          entry.userId ? String(entry.userId.substring(0, 6)) : '-',
          entry.userName ? String(entry.userName) : '-',
        ]);
        setSimplePdfData({
          title: 'ONBOARD',
          subtitle: 'Relatório de Entradas',
          filters: periodString || '',
          headers: headerSimplePdf,
          rows: rowsSimplePdf,
        });
      }
    } else if (entrysGroupedList && entrysGroupedList?.length > 0) {
      rowsSimplePdf = stableSort(
        entrysGroupedList,
        getComparator('asc', 'position'),
      ).map((entry) => {
        const row = [
          `${entry.position || '--'}`,
          `R$ ${entry.totalValue?.toLocaleString('pt-BR', {
            minimumFractionDigits: 2,
          })}`,
          `${entry.count}`,
        ];
        if (selectedItems.find((e) => e.key === 'usedDate')) {
          row.push(
            `${
              selectedItems.find((e) => e.key === 'usedDate')
                ? entry.usedDate || ''
                : ''
            }`,
          );
        }
        if (selectedItems.find((e) => e.key === 'paymentType')) {
          row.push(
            `${
              selectedItems.find((e) => e.key === 'paymentType')
                ? getPaymentType(entry.paymentType)
                : ''
            }`,
          );
        }
        if (selectedItems.find((e) => e.key === 'dbdId')) {
          row.push(`${entry.dbdId}`);
        }
        if (selectedItems.find((e) => e.key === 'qrcodeType')) {
          row.push(
            `${
              selectedItems.find((e) => e.key === 'qrcodeType')
                ? formatQrcodeType(entry.qrcodeType)
                : ''
            }`,
          );
        }
        if (transportOperatorSelected.type === 1) {
          row.push(
            `${
              transportOperatorsList.find(
                (e) => e.id === entry?.transportOperatorId,
              )?.name
            }`,
          );
        }
        return row;
      });
      setSimplePdfData({
        title: 'ONBOARD',
        subtitle: 'Relatório de Entradas',
        filters: periodString || '',
        headers: headerSimplePdf,
        rows: rowsSimplePdf,
      });
    }
  }, [entrysGroupedList, chartEntryReportSimplifiedList]);

  const exportToPDFTitle = useCallback(
    () => (currNavGuide === 0 ? 'detalhado' : 'agrupado'),
    [currNavGuide],
  );
  useEffect(() => {
    if (isPrinting && !dialog.open) {
      dispatch(
        DialogsActions.setSecondOpenLoading({
          title: 'Carregando todos os dados da tabela para montar o pdf',
        }),
      );
    } else {
      dispatch(DialogsActions.setSecondClose({}));
    }
  }, [isPrinting]);

  useEffect(() => {
    if (!isPrinting) {
      setComponent(
        <TableChart
          entrysList={entrysList}
          isPrinting={isPrinting}
          isMounted={isMounted}
          setIsMounted={setIsMounted}
        />,
      );
    } else {
      setComponent(
        <TableToPrint
          entrysList={entrysList}
          isPrinting={isPrinting}
          isMounted={isMounted}
          setIsMounted={setIsMounted}
          setTableReference={setTableReference}
          setIsComponentMounted={setIsComponentMounted}
        />,
      );
    }
  }, [isPrinting]);

  return (
    <Grid className={classes.gridContainer}>
      <Grid container justifyContent='flex-end' alignItems='center'>
        <EntryResume
          currNavGuide={currNavGuide}
          quantityEntry={quantityEntry}
          quantityEntryGrouped={quantityEntryGrouped}
          totalValue={totalValue}
          totalValueGrouped={totalValueGrouped}
        />
        <DialogueExport
          tableInstance={tableReference}
          isPrinting={isPrinting}
          setIsPrinting={setIsPrinting}
          csvRef={csvRef}
          pdfName={`tabela relatorio de entradas_${exportToPDFTitle()}`}
          setIsMounted={setIsMounted}
          setIsComponentMounted={setIsComponentMounted}
          simplePdf
          simplePdfData={simplePdfData}
          columnsPerPage={14}
          headerFontSize={7}
          bodyFontSize={7}
          csv
        />
      </Grid>

      {chartEntryReportSimplifiedList?.length > 0 && (
        <Box width='100%' display='flex' justifyContent='flex-end'>
          <Suspense fallback='Carregando...'>
            <TotalCountComponent
              count={chartEntryReportSimplifiedList?.length || 0}
              title='entradas'
              position='right'
            />
          </Suspense>
        </Box>
      )}

      {/* Navigation Guide */}
      <Box>
        <CustomTabs
          setCurrNavGuide={setCurrNavGuide}
          currNavGuide={currNavGuide}
          labels={['Detalhado', 'Agrupado']}
        />
      </Box>
      <SwipeableViews index={currNavGuide} onChangeIndex={setCurrNavGuide}>
        {/* Detalhado */}
        <TabPanel
          index={0}
          value={currNavGuide}
          boxStyles={{ overflowY: 'hidden' }}>
          {Component}
        </TabPanel>
        {/* Agrupado */}
        <TabPanel
          index={1}
          value={currNavGuide}
          boxStyles={{ overflowY: 'hidden' }}>
          <TableAgrupmentsChart entrysGroupedList={entrysGroupedList} />
        </TabPanel>
      </SwipeableViews>
    </Grid>
  );
};
export { ChartReport };
export default ChartReport;
